import {useState, ChangeEvent, useCallback} from 'react';
import {AppConfiguration, PakConfiguration} from './pineridge-channel';
import {TransitionModal} from './transition-modal';
import {TEXT_INPUT_STYLE, BUTTON_STYLE_MAIN_FLEX, BUTTON_STYLE_CANCEL_FLEX, BUTTON_STYLE_MODAL} from './styles';
import {PakConfigInputs} from './pak-config-inputs';

interface AppConfigModalProps {
  isOpen: boolean;
  setOpen: (isOpen: boolean) => void;
  initialConfig: AppConfiguration;
  setConfig: (config: AppConfiguration) => void;
  setUserAgent: (userAgent: string) => void;
}

const MEDIA_SERVICE_PRESETS = {
  eng: 'https://yosemite.eng.cantina.com',
  stage: 'https://yosemite.stage.cantina.com',
  prod: 'https://yosemite.prod.cantina.com',
};

export function AppConfigModal({isOpen, setOpen, initialConfig, setConfig, setUserAgent}: AppConfigModalProps) {
  const [pakConfig, setPakConfig] = useState<PakConfiguration>(initialConfig);
  const [metadata, setMetadata] = useState(initialConfig.metadata);
  const initialUserAgentValue = initialConfig.userAgent ?? '';
  const [userAgentValue, setUserAgentValue] = useState(initialUserAgentValue);
  const [isUserAgentSet, setIsUserAgentSet] = useState(false);
  const [hasInputError, setHasInputError] = useState(false);

  const initialMediaServiceUrl = initialConfig.mediaServiceUrl ?? MEDIA_SERVICE_PRESETS.stage;
  const [mediaServiceUrl, setMediaServiceUrl] = useState(initialMediaServiceUrl);
  const [selectedEnv, setSelectedEnv] = useState<string>(() => {
    if (initialConfig.mediaServiceEnv) {
      return initialConfig.mediaServiceEnv;
    }
    const url = initialConfig.mediaServiceUrl ?? MEDIA_SERVICE_PRESETS.stage;
    if (url === MEDIA_SERVICE_PRESETS.eng) return 'eng';
    if (url === MEDIA_SERVICE_PRESETS.stage) return 'stage';
    if (url === MEDIA_SERVICE_PRESETS.prod) return 'prod';
    return 'custom';
  });

  function cancel(): void {
    setPakConfig(initialConfig);
    setUserAgentValue(isUserAgentSet ? userAgentValue : initialUserAgentValue);
    setMediaServiceUrl(initialMediaServiceUrl);
    setSelectedEnv(() => {
      if (initialConfig.mediaServiceEnv) {
        return initialConfig.mediaServiceEnv;
      }
      const url = initialConfig.mediaServiceUrl ?? MEDIA_SERVICE_PRESETS.stage;
      if (url === MEDIA_SERVICE_PRESETS.eng) return 'eng';
      if (url === MEDIA_SERVICE_PRESETS.stage) return 'stage';
      if (url === MEDIA_SERVICE_PRESETS.prod) return 'prod';
      return 'custom';
    });
    setHasInputError(false);

    setOpen(false);
  }

  function submit(): void {
    let finalMediaServiceUrl: string | undefined;
    if (selectedEnv === 'custom') {
      finalMediaServiceUrl = mediaServiceUrl && mediaServiceUrl.trim().length > 0 ? mediaServiceUrl : undefined;
    } else {
      finalMediaServiceUrl = MEDIA_SERVICE_PRESETS[selectedEnv as keyof typeof MEDIA_SERVICE_PRESETS];
    }

    const newConfig = {
      ...pakConfig,
      metadata,
      userAgent: userAgentValue && userAgentValue.length > 0 ? userAgentValue : undefined,
      mediaServiceUrl: finalMediaServiceUrl,
      mediaServiceEnv: selectedEnv,
    };

    const mediaServiceUrlChanged = initialConfig.mediaServiceUrl !== finalMediaServiceUrl;

    setConfig(newConfig);
    setOpen(false);

    if (mediaServiceUrlChanged) {
      window.location.reload();
    }
  }

  const handleUpdatePakConfig = useCallback((config: PakConfiguration) => {
    setPakConfig(config);
  }, []);

  const handlePakConfigError = useCallback((hasError: boolean) => {
    setHasInputError(hasError);
  }, []);

  function handleChangeMetadata(e: ChangeEvent<HTMLTextAreaElement>): void {
    const value = e.target.value;
    setMetadata(value);
  }

  function handleChangeUserAgent(e: ChangeEvent<HTMLInputElement>): void {
    const value = e.target.value;
    setUserAgentValue(value);
  }

  function handleSetUserAgent(): void {
    if (userAgentValue && !isUserAgentSet) {
      setUserAgent(userAgentValue);
      setIsUserAgentSet(true);
    }
  }

  function handleChangeEnvironment(e: ChangeEvent<HTMLSelectElement>): void {
    const env = e.target.value;
    setSelectedEnv(env);
    if (env === 'custom') {
      const currentUrl = mediaServiceUrl;
      const isPreset = Object.values(MEDIA_SERVICE_PRESETS).includes(currentUrl);
      if (isPreset) {
        setMediaServiceUrl('');
      }
    } else {
      setMediaServiceUrl(MEDIA_SERVICE_PRESETS[env as keyof typeof MEDIA_SERVICE_PRESETS]);
    }
  }

  function handleChangeMediaServiceUrl(e: ChangeEvent<HTMLInputElement>): void {
    const value = e.target.value;
    setMediaServiceUrl(value);
  }

  return (
    <TransitionModal isOpen={isOpen} onClose={cancel} title="Configure App Defaults">
      <PakConfigInputs
        isOpen={isOpen}
        initialConfig={initialConfig}
        updateConfig={handleUpdatePakConfig}
        updateConfigError={handlePakConfigError}
        disabled={false}
      />

      <div className="mt-2 text-sm grid grid-cols-3">
        <div className="mr-1 mb-1 font-bold">Media Service</div>
        <div className="mr-1 mb-1">
          <select className={TEXT_INPUT_STYLE} value={selectedEnv} onChange={handleChangeEnvironment}>
            <option value="eng">Engineering</option>
            <option value="stage">Stage</option>
            <option value="prod">Production</option>
            <option value="custom">Custom</option>
          </select>
        </div>
        <div></div>

        {selectedEnv === 'custom' && (
          <>
            <div className="mr-1 mb-1 font-bold">Custom URL</div>
            <div className="mr-1 mb-1 col-span-2">
              <input
                className={TEXT_INPUT_STYLE}
                value={mediaServiceUrl}
                onChange={handleChangeMediaServiceUrl}
                placeholder="https://yosemite.example.com"
              />
            </div>
          </>
        )}
      </div>

      <div className="mt-2 text-sm grid grid-cols-3">
        <div className="mr-1 mb-1 font-bold">User Agent</div>
        <div className="mr-1 mb-1">
          <input
            className={TEXT_INPUT_STYLE}
            value={userAgentValue}
            onChange={handleChangeUserAgent}
            disabled={isUserAgentSet}
          />
        </div>
        <div className="mb-1">
          <button
            onClick={handleSetUserAgent}
            disabled={isUserAgentSet || !userAgentValue || userAgentValue.length === 0}
            className={BUTTON_STYLE_MODAL}
          >
            Set
          </button>
        </div>

        <div className="mr-1 mb-1 font-bold">Metadata</div>
        <div>{/* This is just here to fill out the grid row */}</div>
        <div>{/* This is just here to fill out the grid row */}</div>
        <div className="col-span-3">
          <textarea className={TEXT_INPUT_STYLE} value={metadata} onChange={handleChangeMetadata} />
        </div>
      </div>
      <div className="px-4 py-2 flex flex-row-reverse sm:px-6">
        <button type="button" className={BUTTON_STYLE_MAIN_FLEX + ' m-1'} onClick={submit} disabled={hasInputError}>
          Submit
        </button>
        <button type="button" className={BUTTON_STYLE_CANCEL_FLEX + ' m-1'} onClick={cancel}>
          Cancel
        </button>
      </div>
    </TransitionModal>
  );
}
